import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Send, ArrowLeft, Plus, Loader2, Trash2, Clock, Eye,
  Image, Upload, RefreshCw, Mail, Edit3, Copy, Users, Search
} from 'lucide-react';
import RichEmailEditor from './RichEmailEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { value: 'all_leads', label: 'All Leads' },
  { value: 'lead_stage', label: 'Leads by Stage' },
  { value: 'all_users', label: 'All Registered Users' },
  { value: 'everyone', label: 'Everyone (Leads + Users)' },
];

const STAGES = ['new', 'contacted', 'demo', 'trial', 'subscribed', 'churned'];

export default function NewsletterPanel() {
  const [view, setView] = useState('list');
  const [newsletters, setNewsletters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Editor state
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audience, setAudience] = useState('all_leads');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => { loadNewsletters(); }, []);

  const loadNewsletters = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletter`);
      setNewsletters(res.data.newsletters || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/newsletter/templates`);
      setTemplates(res.data.templates || []);
    } catch (e) {}
  };

  const handleNew = () => {
    setCurrent(null);
    setSubject('');
    setBodyHtml('');
    setAudience('all_leads');
    setAudienceFilter('');
    setScheduledAt('');
    setView('editor');
    loadTemplates();
  };

  const handleEdit = async (nl) => {
    setCurrent(nl);
    setSubject(nl.subject);
    setBodyHtml(nl.body_html);
    setAudience(nl.audience || 'all_leads');
    setAudienceFilter(nl.audience_filter || '');
    setScheduledAt(nl.scheduled_at || '');
    setView('editor');
    loadTemplates();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { subject, body_html: bodyHtml, audience, audience_filter: audienceFilter, scheduled_at: scheduledAt || null };
      if (current) {
        await axios.put(`${API}/newsletter/${current.id}`, data);
      } else {
        const res = await axios.post(`${API}/newsletter`, data);
        setCurrent(res.data);
      }
      loadNewsletters();
      alert('Newsletter saved!');
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (!current) { await handleSave(); return; }
    if (!window.confirm('Send this newsletter to your audience now?')) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter/${current.id}/send`);
      alert('Newsletter sending started! Check back for delivery stats.');
      loadNewsletters();
      setView('list');
    } catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const handleTestSend = async () => {
    if (!current) { alert('Save the newsletter first'); return; }
    try {
      const res = await axios.post(`${API}/newsletter/${current.id}/send-test`, {});
      alert(`Test email sent to ${res.data.to}`);
    } catch (e) { alert(e.response?.data?.detail || 'Test send failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this newsletter?')) return;
    try {
      await axios.delete(`${API}/newsletter/${id}`);
      loadNewsletters();
    } catch (e) {}
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/newsletter/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      setBodyHtml(prev => prev + `<img src="${url}" alt="${file.name}" style="max-width:100%;border-radius:8px;margin:12px 0;" />`);
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setImageUploading(false); e.target.value = ''; }
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBodyHtml(tpl.html);
    setShowTemplates(false);
  };

  const statusBadge = (status) => {
    const styles = { draft: 'bg-gray-100 text-gray-700', sending: 'bg-amber-100 text-amber-800', sent: 'bg-green-100 text-green-800' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
  };

  // ─── EDITOR VIEW ───
  if (view === 'editor') {
    return (
      <div data-testid="newsletter-editor">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => { setView('list'); loadNewsletters(); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
            <h2 className="font-heading text-xl font-bold text-[#1B3A6B]">{current ? 'Edit Newsletter' : 'New Newsletter'}</h2>
            {current && statusBadge(current.status)}
          </div>
          <div className="flex gap-2">
            {current && current.status === 'draft' && (
              <button onClick={handleTestSend} className="px-3 py-2 bg-white border border-[#E2DFD8] rounded-lg text-sm text-[#475569] hover:bg-[#F4F2EC] flex items-center gap-1.5" data-testid="newsletter-test">
                <Eye className="w-3.5 h-3.5" /> Test Send
              </button>
            )}
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-white border border-[#E2DFD8] rounded-lg text-sm font-medium text-[#1B3A6B] hover:bg-[#F4F2EC] flex items-center gap-1.5" data-testid="newsletter-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />} Save Draft
            </button>
            <button onClick={handleSend} disabled={sending || !subject} className="px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] flex items-center gap-1.5 disabled:opacity-50" data-testid="newsletter-send">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Now
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Settings */}
          <div className="space-y-4">
            {/* Templates */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <button onClick={() => { loadTemplates(); setShowTemplates(!showTemplates); }} className="w-full text-left text-sm font-medium text-[#0F1A2A] flex items-center justify-between" data-testid="newsletter-templates-toggle">
                <span className="flex items-center gap-2"><Copy className="w-4 h-4 text-[#B8860B]" /> Use Template</span>
                <span className="text-xs text-[#94A3B8]">{showTemplates ? 'Hide' : 'Show'}</span>
              </button>
              {showTemplates && (
                <div className="mt-3 space-y-1.5">
                  {templates.map((t, i) => (
                    <button key={i} onClick={() => applyTemplate(t)} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-[#F4F2EC] text-[#475569] transition-colors" data-testid={`tpl-${i}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Audience */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider block mb-2">Audience</label>
              <select value={audience} onChange={e => setAudience(e.target.value)} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm" data-testid="newsletter-audience">
                {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              {audience === 'lead_stage' && (
                <select value={audienceFilter} onChange={e => setAudienceFilter(e.target.value)} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm mt-2" data-testid="newsletter-stage-filter">
                  <option value="">Select stage</option>
                  {STAGES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              )}
            </div>

            {/* Schedule */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider block mb-2">Schedule (optional)</label>
              <input type="datetime-local" value={scheduledAt ? scheduledAt.slice(0, 16) : ''} onChange={e => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : '')} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm" data-testid="newsletter-schedule" />
              <p className="text-[10px] text-[#94A3B8] mt-1">Leave empty to send manually</p>
            </div>

            {/* Image Upload */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-xs text-[#94A3B8] uppercase tracking-wider block mb-2">Insert Image</label>
              <label className="flex items-center gap-2 px-3 py-2.5 bg-[#F9F8F6] rounded-lg cursor-pointer hover:bg-[#F4F2EC] text-sm text-[#475569]" data-testid="newsletter-upload-image">
                {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {imageUploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
              <p className="text-[10px] text-[#94A3B8] mt-1">PNG, JPG, GIF — max 5MB. Inserted at end of content.</p>
            </div>
          </div>

          {/* Middle + Right: Editor & Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Subject */}
              <div className="px-5 py-3 border-b border-[#E2DFD8]">
                <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Newsletter subject line *" className="w-full text-lg font-medium text-[#0F1A2A] border-none focus:ring-0 focus:outline-none" data-testid="newsletter-subject" />
              </div>
              {/* Rich Editor */}
              <div className="p-4">
                <RichEmailEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write your newsletter content here..." />
              </div>
              {/* Preview */}
              <div className="border-t border-[#E2DFD8] p-4 bg-[#F4F2EC]">
                <p className="text-xs text-[#94A3B8] mb-2">Preview</p>
                <div className="bg-white rounded-lg p-5 shadow-sm max-h-[300px] overflow-y-auto" data-testid="newsletter-preview">
                  <div style={{ fontFamily: 'Georgia, serif', maxWidth: 500, margin: '0 auto' }}>
                    <div style={{ background: '#1B3A6B', padding: '16px 20px', borderRadius: '8px 8px 0 0', textAlign: 'center' }}>
                      <div style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>dsashun</div>
                      <div style={{ color: '#B8860B', fontSize: 10, letterSpacing: 1 }}>AI-Powered Bible Study Tools</div>
                    </div>
                    <div style={{ padding: '20px', border: '1px solid #E2DFD8', borderTop: 'none' }}>
                      {subject && <div style={{ color: '#1B3A6B', fontWeight: 'bold', fontSize: 15, marginBottom: 12 }}>{subject.replace(/\{\{name\}\}/g, 'John')}</div>}
                      <div style={{ fontSize: 13, lineHeight: 1.7, color: '#0F1A2A' }} dangerouslySetInnerHTML={{ __html: (bodyHtml || '<p style="color:#94A3B8">Newsletter content...</p>').replace(/\{\{name\}\}/g, 'John') }} />
                    </div>
                    <div style={{ background: '#F9F8F6', padding: '12px', borderRadius: '0 0 8px 8px', textAlign: 'center', border: '1px solid #E2DFD8', borderTop: 'none' }}>
                      <span style={{ color: '#94A3B8', fontSize: 10 }}>Sent via dsashun</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div data-testid="newsletter-list">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Newsletters</h2>
        <button onClick={handleNew} className="flex items-center gap-1.5 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="newsletter-new">
          <Plus className="w-4 h-4" /> New Newsletter
        </button>
      </div>

      {loading ? <div className="text-center py-12 text-[#475569]">Loading...</div> : newsletters.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <Mail className="w-10 h-10 text-[#E2DFD8] mx-auto mb-3" />
          <p className="text-[#475569] font-medium">No newsletters yet</p>
          <p className="text-xs text-[#94A3B8] mt-1">Create your first newsletter to engage your audience</p>
          <button onClick={handleNew} className="mt-4 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">Create Newsletter</button>
        </div>
      ) : (
        <div className="space-y-3">
          {newsletters.map(nl => (
            <div key={nl.id} className="bg-white rounded-xl p-5 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow" data-testid={`nl-${nl.id}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleEdit(nl)}>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-sm text-[#0F1A2A] truncate">{nl.subject || 'Untitled'}</h3>
                  {statusBadge(nl.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                  <span>{new Date(nl.created_at).toLocaleDateString()}</span>
                  {nl.sent_count > 0 && <span className="text-green-600">{nl.sent_count} sent</span>}
                  {nl.failed_count > 0 && <span className="text-red-500">{nl.failed_count} failed</span>}
                  <span className="capitalize">{nl.audience?.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => handleEdit(nl)} className="p-2 hover:bg-[#F4F2EC] rounded-lg" title="Edit"><Edit3 className="w-4 h-4 text-[#475569]" /></button>
                <button onClick={() => handleDelete(nl.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
